import type { DateTime } from 'luxon'

import type { PtuResult } from '@/features/bidding/components/BidVersionPtusDataGrid'
import { ZERO_VOLUME } from '@/features/bidding/components/BidVersionPtusDataGrid'
import type { ExtendedBid, Forecast, PtuChunk } from '@/features/bidding/types/bid'
import { calculateAverageVolume } from '@/features/bidding/utils/calculations/calculateAverageVolume'
import { findAcceptedVolumeForTime, findVolumeForTime } from '@/features/bidding/utils/findVolumeForTime'

export const buildBidPtuResultRowItems = (
  bidVersion: ExtendedBid,
  isPtuLessThanAnHour: boolean,
  versionForecast?: Forecast | null,
): PtuResult[] => {
  const groupedByHour = groupPtuResultsByHour(bidVersion, isPtuLessThanAnHour, versionForecast)

  const results: PtuResult[] = []

  for (const [parentPtu, ptuResults] of groupedByHour.entries()) {
    const offeredVolumes = ptuResults.map((ptuResult) => ptuResult.offeredVolume)
    const acceptedVolumes = ptuResults
      .map((ptuResult) => ptuResult.acceptedVolume)
      .filter((volume) => volume != undefined)
    const forecastedVolumes =
      ptuResults
        .map((ptuResult) => findVolumeForTime(ptuResult.ptu.start, versionForecast?.forecastPtus ?? []))
        .filter((volume) => volume != undefined) ?? []
    const ptuStart = ptuResults[0].ptu.start.startOf('hour')

    // Create the parent row that displays average volumes only if the PTU duration is less than 60 minutes
    if (isPtuLessThanAnHour) {
      results.push({
        hierarchy: [parentPtu],
        ptu: { start: ptuStart, end: ptuStart.plus({ hours: 1 }) },
        offeredVolume: calculateAverageVolume(offeredVolumes) ?? ZERO_VOLUME,
        forecastedVolume: calculateAverageVolume(forecastedVolumes),
        acceptedVolume: calculateAverageVolume(acceptedVolumes),
        chunks: buildParentRowPtuChunks(ptuResults),
      })
    }

    // Add child rows that display the actual volumes per PTU
    results.push(...ptuResults)
  }

  return results
}

const groupPtuResultsByHour = (
  bidVersion: ExtendedBid,
  isPtuLessThanAnHour: boolean,
  versionForecast?: Forecast | null,
) => {
  const groupedByHour = new Map<string, PtuResult[]>()

  for (const ptu of bidVersion.ptus) {
    const parentPtu = ptu.ptu.start.startOf('hour').toISO() ?? ptu.ptu.start.startOf('hour').toString()
    const ptuResult: PtuResult = {
      hierarchy: [parentPtu, ...childPtu(ptu.ptu.start, isPtuLessThanAnHour)],
      ptu: ptu.ptu,
      offeredVolume: ptu.offeredVolume,
      forecastedVolume: versionForecast ? findVolumeForTime(ptu.ptu.start, versionForecast.forecastPtus) : undefined,
      acceptedVolume: findAcceptedVolumeForTime(ptu.ptu.start, bidVersion.ptus),
      chunks: ptu.ptuChunks,
    }
    if (!groupedByHour.has(parentPtu)) {
      groupedByHour.set(parentPtu, [])
    }
    groupedByHour.get(parentPtu)?.push(ptuResult)
  }

  return groupedByHour
}

const childPtu = (ptuStartTime: DateTime, isPtuLessThanAnHour: boolean) => {
  const parentPtuFormatted = ptuStartTime.toFormat('HH:00')
  const childPtuFormatted = ptuStartTime.toFormat('HH:mm')
  const childPtuIso = ptuStartTime.toISO() ?? ptuStartTime.toString()
  return parentPtuFormatted === childPtuFormatted && !isPtuLessThanAnHour ? [] : [childPtuIso]
}

const buildParentRowPtuChunks = (ptuResults: PtuResult[]): PtuChunk[] => {
  const groupedByPrice = groupByOfferedPrice(ptuResults)

  const results: PtuChunk[] = []

  for (const [offeredPrice, ptuChunks] of groupedByPrice.entries()) {
    const offeredVolumes = ptuChunks.map((chunk) => chunk.offeredVolume)
    const acceptedVolumes = ptuChunks
      .map((chunk) => chunk.acceptedVolume)
      .filter((volume) => volume !== null && volume !== undefined)
    results.push({
      offeredPrice: offeredPrice,
      offeredVolume: calculateAverageVolume(offeredVolumes) ?? ZERO_VOLUME,
      acceptedVolume: calculateAverageVolume(acceptedVolumes),
    })
  }

  return results
}

const groupByOfferedPrice = (ptuResults: PtuResult[]): Map<number, PtuChunk[]> => {
  const groupedByPrice = new Map<number, PtuChunk[]>()

  for (const ptu of ptuResults) {
    if (ptu.chunks) {
      for (const chunk of ptu.chunks) {
        if (!groupedByPrice.has(chunk.offeredPrice)) {
          groupedByPrice.set(chunk.offeredPrice, [])
        }
        groupedByPrice.get(chunk.offeredPrice)?.push(chunk)
      }
    }
  }

  return groupedByPrice
}
